import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/stores/ClinicContext';
import { useAuth } from '@/hooks/useAuth';
import { PatientService } from '@/services/firebase/patients';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { DateInput } from '@/components/ui/DateInput';
import { Button } from '@/components/ui/Button';
import { DocumentCapture } from '@/components/patients/DocumentCapture';
import type { ExtractedPatientData } from '@/services/patients/documentOcr';
import type { Patient, PatientIdDocument } from '@/types/models';
import { AlertTriangle, ExternalLink, UserCheck } from 'lucide-react';
import { parseBirthDateInput } from '@/utils/birthDate';
import { formatCPF } from '@/utils/formatters';
import { isValidCPF } from '@/utils/validators';
import { isFullName, isUsablePhone } from '@/utils/accompanistRequirements';
import { PatientMetaLines } from '@/components/patients/PatientMetaLines';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Chamado após criar o paciente. `openTab` indica em qual aba da ficha
   *  abrir (ex.: 'cadastro' quando o usuário pediu o cadastro completo). */
  onSuccess: (patientId: string, opts?: { openTab?: string }) => void;
  /** Pre-fill the name field when opening the modal from a failed search. */
  initialName?: string;
  /** Pre-fill the CPF field (e.g. when opening from a failed CPF search). */
  initialCpf?: string;
  /**
   * Exige os mínimos de identificação — nome completo, CPF e telefone — em vez
   * de só o nome. Usado no cadastro rápido do ACOMPANHANTE do dia, que assina
   * a declaração de acompanhante e precisa dos três (ver
   * `utils/accompanistRequirements`).
   */
  requireFullIdentity?: boolean;
  /** Título do modal (default "Novo Paciente"). */
  title?: string;
}

export const PatientFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialName,
  initialCpf,
  requireFullIdentity = false,
  title = 'Novo Paciente',
}: Props) => {
  const navigate = useNavigate();
  const { clinicId } = useClinic();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompleteMode, setIsCompleteMode] = useState(false);
  // Feedback do botão "Abrir cadastro completo" — cria o paciente e navega
  // pra aba Cadastro da ficha. Estado próprio pra não se confundir com o
  // spinner do botão "Salvar Paciente".
  const [openingFull, setOpeningFull] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cpf: '',
    birthDate: '',
    email: '',
    gender: '',
    rg: '',
    cns: '',
  });
  const [isMinor, setIsMinor] = useState(false);
  const [guardian, setGuardian] = useState({
    name: '',
    cpf: '',
    relationship: 'RESPONSAVEL' as 'PAI' | 'MAE' | 'RESPONSAVEL',
    phone: '',
  });
  const [lgpdConsent, setLgpdConsent] = useState(false);

  // Document capture state
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [capturedType, setCapturedType] = useState<PatientIdDocument['type']>('OUTRO');
  const [ocrExtracted, setOcrExtracted] = useState(false);

  // Duplicate detection
  const [duplicates, setDuplicates] = useState<
    Array<{ patient: Patient; reason: 'cpf' | 'phone' | 'name' }> | null
  >(null);
  const [duplicateSource, setDuplicateSource] = useState<'ocr' | 'form'>('form');
  // After the user acknowledges duplicates (e.g. "Cadastrar homônimo"), don't
  // re-run the check on submit — we already know and they chose to proceed.
  const [duplicatesAcknowledged, setDuplicatesAcknowledged] = useState(false);

  // Live suggestions while typing (prefix match on name/CPF)
  const [nameMatches, setNameMatches] = useState<Patient[]>([]);
  const [cpfMatches, setCpfMatches] = useState<Patient[]>([]);
  const [liveDismissed, setLiveDismissed] = useState(false);

  // Pre-fill name when opening via "Cadastrar" from search results
  useEffect(() => {
    if (isOpen && initialName) {
      setFormData((prev) => ({ ...prev, name: initialName }));
    }
  }, [isOpen, initialName]);

  // Pre-fill CPF when opening from a failed CPF search — expand to complete
  // mode so the CPF field is visible.
  useEffect(() => {
    if (isOpen && initialCpf) {
      setFormData((prev) => ({ ...prev, cpf: formatCPF(initialCpf) }));
      setIsCompleteMode(true);
    }
  }, [isOpen, initialCpf]);

  // Live name search (prefix match) — fires while user types
  useEffect(() => {
    if (!isOpen || !clinicId || liveDismissed) {
      setNameMatches([]);
      return;
    }
    const trimmed = formData.name.trim();
    if (trimmed.length < 3) {
      setNameMatches([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await PatientService.searchPatients(clinicId, trimmed, 'name');
        if (!cancelled) setNameMatches(results.slice(0, 5));
      } catch (err) {
        console.warn('Live name search failed:', err);
        if (!cancelled) setNameMatches([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.name, clinicId, isOpen, liveDismissed]);

  // Live CPF search (prefix or exact) — fires while user types
  useEffect(() => {
    if (!isOpen || !clinicId || liveDismissed) {
      setCpfMatches([]);
      return;
    }
    const digits = formData.cpf.replace(/\D/g, '');
    if (digits.length < 3) {
      setCpfMatches([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await PatientService.searchPatients(clinicId, digits, 'cpf');
        if (!cancelled) setCpfMatches(results.slice(0, 5));
      } catch (err) {
        console.warn('Live CPF search failed:', err);
        if (!cancelled) setCpfMatches([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.cpf, clinicId, isOpen, liveDismissed]);

  // Merge name + cpf matches (dedup by id) to render a single suggestion list
  const liveSuggestions = (() => {
    const map = new Map<string, Patient>();
    for (const p of cpfMatches) map.set(p.id, p);
    for (const p of nameMatches) if (!map.has(p.id)) map.set(p.id, p);
    return Array.from(map.values()).slice(0, 5);
  })();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // CPF com máscara 000.000.000-00 — trava em 11 dígitos (não dá pra
    // digitar a mais; a validação do salvar barra a menos).
    setFormData((prev) => ({ ...prev, [name]: name === 'cpf' ? formatCPF(value) : value }));
    setError(null);
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', cpf: '', birthDate: '', email: '', gender: '', rg: '', cns: '' });
    setGuardian({ name: '', cpf: '', relationship: 'RESPONSAVEL', phone: '' });
    setIsMinor(false);
    setLgpdConsent(false);
    setError(null);
    setIsCompleteMode(false);
    setCapturedFile(null);
    setCapturedType('OUTRO');
    setOcrExtracted(false);
    setDuplicates(null);
    setDuplicatesAcknowledged(false);
    setNameMatches([]);
    setCpfMatches([]);
    setLiveDismissed(false);
    setOpeningFull(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Check if patient is a minor based on birth date
  const checkMinorStatus = (birthDate: string) => {
    if (!birthDate) return;
    const epoch = parseBirthDateInput(birthDate);
    if (epoch == null) return;
    const birth = new Date(epoch);
    const today = new Date();
    const age = today.getFullYear() - birth.getUTCFullYear();
    const monthDiff = today.getMonth() - birth.getUTCMonth();
    const isUnder18 = age < 18 || (age === 18 && monthDiff < 0);
    setIsMinor(isUnder18);
  };

  // ── Document capture callback ─────────────────────────────────────
  const handleExtracted = async (file: File, extracted: ExtractedPatientData) => {
    setCapturedFile(file);
    setCapturedType(extracted.documentType || 'OUTRO');
    setOcrExtracted(true);

    // Auto-fill form fields — don't overwrite what the user has already typed
    setFormData((prev) => ({
      ...prev,
      name: prev.name || extracted.name || '',
      cpf: prev.cpf || formatCPF(extracted.cpf ?? ''),
      rg: prev.rg || (extracted.rg ?? ''),
      birthDate: prev.birthDate || (extracted.birthDate ?? ''),
      gender: prev.gender || (extracted.gender ?? ''),
    }));

    // Trigger minor-status update if birthDate came from OCR
    if (extracted.birthDate) {
      checkMinorStatus(extracted.birthDate);
    }

    // If OCR gave us a CPF or name, check for existing patient
    if (clinicId && (extracted.cpf || extracted.name)) {
      try {
        const matches = await PatientService.checkDuplicates(
          clinicId,
          extracted.name || '',
          undefined,
          extracted.cpf,
        );
        if (matches.length > 0) {
          setDuplicates(matches);
          setDuplicateSource('ocr');
        }
      } catch (err) {
        console.warn('Duplicate check failed:', err);
      }
    }
  };

  const handleClearDocument = () => {
    setCapturedFile(null);
    setOcrExtracted(false);
  };

  const handleSubmit = async (target: 'stay' | 'fullCadastro' = 'stay') => {
    // Validation — only name is required now
    if (!formData.name.trim()) {
      setError('O nome do paciente é obrigatório.');
      return;
    }

    // Cadastro de acompanhante: nome completo, CPF e telefone são os mínimos —
    // sem eles a declaração de acompanhante sai incompleta.
    if (requireFullIdentity && !isFullName(formData.name)) {
      setError('Informe o nome completo do acompanhante (nome e sobrenome).');
      return;
    }

    // CPF (quando preenchido) só entra no padrão: exatamente 11 dígitos e
    // dígitos verificadores corretos. A máscara já impede digitar a mais;
    // aqui barramos incompleto e erro de digitação.
    const cpfDigits = formData.cpf.replace(/\D/g, '');
    if (requireFullIdentity && !cpfDigits) {
      setError('O CPF do acompanhante é obrigatório.');
      return;
    }
    if (requireFullIdentity && !isUsablePhone(formData.phone)) {
      setError(
        formData.phone.trim()
          ? 'Telefone inválido — informe DDD + número (10 ou 11 dígitos).'
          : 'O telefone do acompanhante é obrigatório.',
      );
      return;
    }
    if (cpfDigits && cpfDigits.length !== 11) {
      setError(`CPF incompleto: o CPF tem 11 dígitos (foram digitados ${cpfDigits.length}).`);
      return;
    }
    if (cpfDigits && !isValidCPF(cpfDigits)) {
      setError('CPF inválido — confira os números digitados.');
      return;
    }
    const guardianCpfDigits = guardian.cpf.replace(/\D/g, '');
    if (guardianCpfDigits && guardianCpfDigits.length !== 11) {
      setError(`CPF do responsável incompleto: o CPF tem 11 dígitos (foram digitados ${guardianCpfDigits.length}).`);
      return;
    }
    if (guardianCpfDigits && !isValidCPF(guardianCpfDigits)) {
      setError('CPF do responsável inválido — confira os números digitados.');
      return;
    }

    if (!clinicId) {
      setError('Erro: Clínica não identificada. Faça login novamente.');
      return;
    }

    if (!user) {
      setError('Erro: Usuário não autenticado. Faça login novamente.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Re-check for duplicates using typed values (catches manual entries).
      // Skip the re-check if the user has already acknowledged duplicates
      // (e.g. via "Cadastrar homônimo") — we'd just show the same list again.
      if (!duplicates && !duplicatesAcknowledged) {
        const matches = await PatientService.checkDuplicates(
          clinicId,
          formData.name.trim(),
          formData.phone.trim() || undefined,
          cpfDigits || undefined,
        );
        if (matches.length > 0) {
          setDuplicates(matches);
          setDuplicateSource('form');
          setLoading(false);
          return;
        }
      }

      if (isMinor && isCompleteMode && !guardian.name.trim()) {
        setError('Para menores de idade, os dados do responsável legal são obrigatórios.');
        return;
      }
      if (isCompleteMode && !lgpdConsent) {
        setError('O consentimento LGPD é obrigatório para cadastro completo.');
        return;
      }

      const patientData: Record<string, unknown> = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        registrationStatus: isCompleteMode ? 'COMPLETE' : 'MINIMAL',
        createdBy: user.uid,
      };
      // Salva só os 11 dígitos (sem pontuação) — formato canônico do banco.
      if (cpfDigits) patientData.cpf = cpfDigits;
      if (formData.rg.trim()) patientData.rg = formData.rg.trim();
      if (formData.cns.trim()) patientData.cns = formData.cns.trim();
      if (formData.email.trim()) patientData.email = formData.email.trim();
      if (formData.birthDate) {
        const epoch = parseBirthDateInput(formData.birthDate);
        if (epoch != null) patientData.birthDate = epoch;
      }
      if (formData.gender) patientData.gender = formData.gender;
      if (isMinor && guardian.name) {
        patientData.guardians = [{ ...guardian, cpf: guardianCpfDigits }];
      }
      if (lgpdConsent) {
        patientData.lgpdConsentDate = Date.now();
        patientData.lgpdConsentVersion = '1.0';
      }

      const created = (await PatientService.createPatient(
        clinicId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        patientData as any,
      )) as { id: string };

      // Upload the document photo (if any) and attach to the patient record
      if (capturedFile) {
        try {
          await PatientService.uploadIdDocument(
            clinicId,
            created.id,
            capturedFile,
            capturedType,
            { ocrExtracted },
          );
        } catch (uploadErr) {
          console.error('Document upload failed (patient was still created):', uploadErr);
        }
      }

      resetForm();
      onSuccess(created.id, target === 'fullCadastro' ? { openTab: 'cadastro' } : undefined);
    } catch (err: unknown) {
      console.error('Error creating patient:', err);
      const e = err as { code?: string; message?: string };
      if (e?.code === 'permission-denied') {
        setError('Sem permissão para cadastrar pacientes. Verifique se seu usuário está cadastrado como profissional na clínica.');
      } else {
        setError(`Erro ao salvar paciente: ${e?.message || e?.code || 'Erro desconhecido'}.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Duplicate dialog actions ──────────────────────────────────────
  const openExistingPatient = (patientId: string) => {
    resetForm();
    onClose();
    navigate(`/pacientes/${patientId}`);
  };

  const dismissDuplicates = () => {
    setDuplicates(null);
    setDuplicatesAcknowledged(true);
    // User confirmed this is a new patient — open the full registration form
    // so they can fill in all the details that distinguish from the near-match.
    setIsCompleteMode(true);
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title}>
      <div className="space-y-4 py-4">
        {requireFullIdentity && (
          <p className="text-xs text-on-surface-variant bg-surface-container rounded-lg px-3 py-2">
            O acompanhante do dia exige <strong className="text-on-surface">nome completo,
            CPF e telefone</strong> — ele assina a declaração de acompanhante.
          </p>
        )}
        {error && (
          <div className="bg-error-container/20 border border-error-container text-on-error-container rounded-lg px-4 py-3 text-sm animate-fade-in">
            {error}
          </div>
        )}

        {/* Duplicate warning */}
        {duplicates && duplicates.length > 0 && (() => {
          const allHomonym = duplicates.every((d) => d.reason === 'name');
          const headline = allHomonym
            ? `Já existe paciente com este nome${duplicates.length > 1 ? ' (homônimos)' : ''}`
            : `Paciente${duplicates.length > 1 ? 's' : ''} já cadastrado${duplicates.length > 1 ? 's' : ''}`;
          const subhead =
            duplicateSource === 'ocr'
              ? 'O documento analisado parece pertencer a um paciente existente:'
              : allHomonym
                ? 'Confira se o paciente já está no sistema:'
                : 'Encontramos pacientes com dados parecidos:';
          const dismissLabel = allHomonym
            ? 'Cadastrar homônimo'
            : 'Não é este paciente, continuar cadastro';
          return (
            <div className="bg-warning/10 border border-warning/40 rounded-lg p-4 space-y-3 animate-fade-in">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-warning shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-on-surface">{headline}</p>
                  <p className="text-xs text-on-surface-variant">{subhead}</p>
                </div>
              </div>
              <div className="space-y-2">
                {duplicates.map(({ patient: p }) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => openExistingPatient(p.id)}
                    className="w-full flex items-center justify-between gap-2 bg-surface rounded-md px-3 py-2 border border-outline-variant/20 hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-on-surface truncate">{p.name}</div>
                      <PatientMetaLines p={p} className="text-[11px] text-on-surface-variant" />
                    </div>
                    <ExternalLink size={14} className="text-primary shrink-0" />
                  </button>
                ))}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={dismissDuplicates}
                  className="text-xs text-on-surface-variant hover:text-on-surface underline underline-offset-2"
                >
                  {dismissLabel}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Document capture (always available) */}
        <DocumentCapture
          onExtracted={handleExtracted}
          onClear={handleClearDocument}
          disabled={loading}
        />

        <Input
          label="Nome Completo *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Ex: Maria da Silva"
          autoComplete="off"
        />
        <Input
          label={requireFullIdentity ? 'CPF *' : 'CPF'}
          name="cpf"
          value={formData.cpf}
          onChange={handleChange}
          placeholder="000.000.000-00"
          autoComplete="off"
        />

        {/* Live suggestions — pacientes já cadastrados que batem com nome/CPF */}
        {!duplicates && !liveDismissed && liveSuggestions.length > 0 && (
          <div className="bg-primary/5 border border-primary/30 rounded-lg p-3 space-y-2 animate-fade-in">
            <div className="flex items-start gap-2">
              <UserCheck size={16} className="text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-on-surface">
                  {liveSuggestions.length === 1
                    ? 'Já existe um paciente com dados parecidos'
                    : `${liveSuggestions.length} pacientes com dados parecidos`}
                </p>
                <p className="text-xs text-on-surface-variant">
                  Clique para abrir o cadastro existente.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLiveDismissed(true)}
                className="text-xs text-on-surface-variant hover:text-on-surface underline underline-offset-2 shrink-0"
              >
                Ignorar
              </button>
            </div>
            <div className="space-y-1.5">
              {liveSuggestions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openExistingPatient(p.id)}
                  className="w-full flex items-center justify-between gap-2 bg-surface rounded-md px-3 py-2 border border-outline-variant/20 hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-on-surface truncate">{p.name}</div>
                    <PatientMetaLines p={p} className="text-[11px] text-on-surface-variant" />
                  </div>
                  <ExternalLink size={14} className="text-primary shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        <Input
          label={requireFullIdentity ? 'Telefone / WhatsApp *' : 'Telefone / WhatsApp'}
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="(11) 99999-9999"
          autoComplete="off"
        />

        <DateInput
          label="Data de Nascimento"
          name="birthDate"
          value={formData.birthDate}
          onChange={(e) => {
            handleChange(e);
            checkMinorStatus(e.target.value);
          }}
        />

        {isCompleteMode && (
          <div className="animate-fade-in space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="RG"
                name="rg"
                value={formData.rg}
                onChange={handleChange}
                placeholder="00.000.000-0"
              />
              <Input
                label="CNS (Cartão SUS)"
                name="cns"
                value={formData.cns}
                onChange={handleChange}
                placeholder="000 0000 0000 0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Sexo</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={(e) => setFormData((prev) => ({ ...prev, gender: e.target.value }))}
                className="w-full rounded-lg border border-outline-variant/20 bg-surface-container px-3 py-2 text-sm text-on-surface"
              >
                <option value="">Selecionar</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="OTHER">Outro</option>
              </select>
            </div>
            <Input
              label="E-mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="paciente@email.com"
            />

            {/* Guardian fields for minors (T080) */}
            {isMinor && (
              <div className="border border-primary/20 rounded-lg p-4 space-y-3 bg-primary/5">
                <h4 className="text-sm font-semibold text-primary">Responsável Legal (obrigatório para menores)</h4>
                <Input
                  label="Nome do Responsável *"
                  value={guardian.name}
                  onChange={(e) => setGuardian((g) => ({ ...g, name: e.target.value }))}
                  placeholder="Nome completo"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="CPF do Responsável"
                    value={guardian.cpf}
                    onChange={(e) => setGuardian((g) => ({ ...g, cpf: formatCPF(e.target.value) }))}
                    placeholder="000.000.000-00"
                  />
                  <div>
                    <label className="block text-sm font-medium text-on-surface-variant mb-1">Parentesco</label>
                    <select
                      value={guardian.relationship}
                      onChange={(e) =>
                        setGuardian((g) => ({ ...g, relationship: e.target.value as typeof g.relationship }))
                      }
                      className="w-full rounded-lg border border-outline-variant/20 bg-surface-container px-3 py-2 text-sm text-on-surface"
                    >
                      <option value="PAI">Pai</option>
                      <option value="MAE">Mãe</option>
                      <option value="RESPONSAVEL">Responsável</option>
                    </select>
                  </div>
                </div>
                <Input
                  label="Telefone do Responsável"
                  value={guardian.phone}
                  onChange={(e) => setGuardian((g) => ({ ...g, phone: e.target.value }))}
                  placeholder="(11) 99999-9999"
                />
              </div>
            )}

            {/* LGPD consent (T081) */}
            <div className="flex items-start gap-2 mt-4 p-3 border border-outline-variant/20 rounded-lg">
              <input
                type="checkbox"
                id="lgpd"
                checked={lgpdConsent}
                onChange={(e) => setLgpdConsent(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded text-primary focus:ring-primary"
              />
              <label htmlFor="lgpd" className="text-sm text-on-surface-variant">
                {isMinor
                  ? 'O responsável legal autoriza o tratamento dos dados pessoais do menor conforme a LGPD (Lei 13.709/2018).'
                  : 'O paciente autoriza o tratamento dos seus dados pessoais conforme a LGPD (Lei 13.709/2018).'}
              </label>
            </div>
          </div>
        )}

        {!isCompleteMode && (
          <button
            type="button"
            disabled={loading}
            className="text-primary text-sm font-medium hover:underline disabled:opacity-60"
            onClick={() => {
              setOpeningFull(true);
              handleSubmit('fullCadastro').finally(() => setOpeningFull(false));
            }}
          >
            {openingFull ? 'Criando paciente…' : 'Abrir cadastro completo (todos os campos) →'}
          </button>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-6 border-t border-outline-variant/10">
        <Button variant="secondary" onClick={handleClose}>Cancelar</Button>
        <Button variant="primary" onClick={() => handleSubmit('stay')} isLoading={loading}>Salvar Paciente</Button>
      </div>
    </Modal>
  );
};
